import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { closeIcon } from '../../icons'
import './DetailModalCommon.css'
import './DetailQrTagModal.css'
import Loading from '../common/Loading'
import { getFormattedDate } from '../../helpers/timeHelper'
import {
  exportQrTagEventsCsv,
  getQrTagEvents,
} from '../../modules/QrTag'
import { parseHexColor } from '../../utils/colorUtils'
import QrTagEventsMap from './QrTagEventsMap'

type Props = {
  closeModalFunction: (arg: any | void) => void
  modalProps: {
    qrTag: IQrTag
    group: IGroup
    refreshList: () => Promise<void> | void
  }
}

const formatLocation = (event: IQrTagEvent) => {
  if (event.latitude == null && event.longitude == null) {
    return '位置情報なし'
  }

  return `${event.latitude ?? '-'}, ${event.longitude ?? '-'}`
}

const formatRecorder = (event: IQrTagEvent) => {
  const name = event.user?.name ?? event.recorded_by?.name
  if (!name) return '記録者不明'
  return name
}

const hasLocation = (event: IQrTagEvent) => event.latitude != null && event.longitude != null

const buildTimelineItemStyle = (event: IQrTagEvent): React.CSSProperties | undefined => {
  const parsedColor = parseHexColor(event.event_type_color)
  if (!parsedColor) return undefined

  return {
    '--timeline-marker-color': `#${parsedColor.rgb}`,
  } as React.CSSProperties
}

const DetailQrTagModal = ({
  closeModalFunction,
  modalProps,
}: Props) => {
  const { qrTag, group, refreshList } = modalProps
  const [events, setEvents] = useState<IQrTagEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  const fetchEvents = useCallback(async () => {
    if (!isMountedRef.current) return

    try {
      setLoading(true)
      const result = await getQrTagEvents(group.id, qrTag.id, { per: 10 })
      if (!isMountedRef.current) return
      setEvents(result)
      setError(null)
    } catch (err) {
      if (!isMountedRef.current) return
      console.error('Failed to fetch QR tag events', err)
      setEvents([])
      setError('QRタグイベントの取得に失敗しました')
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }, [group.id, qrTag.id])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const eventsWithLocation = useMemo(
    () => events.filter(hasLocation),
    [events],
  )

  useEffect(() => {
    if (!eventsWithLocation.length) {
      setSelectedEventId(null)
      return
    }
    if (!selectedEventId || !eventsWithLocation.some((event) => event.id === selectedEventId)) {
      setSelectedEventId(eventsWithLocation[0]!.id)
    }
  }, [eventsWithLocation, selectedEventId])

  const reloadAll = async () => {
    await fetchEvents()
    await refreshList()
  }

  const downloadCsv = useCallback(async () => {
    if (!isMountedRef.current || downloading) return

    try {
      setDownloading(true)
      setDownloadError(null)
      const blob = await exportQrTagEventsCsv(group.id, qrTag.id)
      if (!isMountedRef.current) return
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `qr_tag_events_${qrTag.id}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      if (!isMountedRef.current) return
      console.error('Failed to export QR tag events', err)
      setDownloadError('CSVのダウンロードに失敗しました')
    } finally {
      if (isMountedRef.current) {
        setDownloading(false)
      }
    }
  }, [downloading, group.id, qrTag.id])

  const handleTimelineSelect = (event: IQrTagEvent) => {
    if (!hasLocation(event)) return
    setSelectedEventId(event.id)
  }

  return (
    <div className="detail-modal-container" data-testid="detail-qr-tag-modal">
      <Loading show={loading || downloading} />
      <div className="overlay" onClick={closeModalFunction} />
      <div className="detail-base">
        <div className="scrollable">
          <div className="title-row">
            <div className="detail-title">
              QRタグ詳細
            </div>
            <div>
              <button
                onClick={closeModalFunction}
                className="close-button"
                type="button"
              >
                <img
                  src={closeIcon}
                  alt="close-button"
                />
              </button>
            </div>
          </div>
          <div className="qr-tag-modal-main">
            <div className="qr-tag-summary">
              <div className="qr-tag-name">{qrTag.name}</div>
              <div className="qr-tag-description">{qrTag.description || '説明なし'}</div>
              <div className="qr-tag-code">
                <span className="label">QRコード番号</span>
                <span>{qrTag.qr_code_info?.digit_code || '-'}</span>
              </div>
              {qrTag.qr_code_info?.image_url && (
                <div className="qr-tag-code-image">
                  <img
                    src={qrTag.qr_code_info.image_url}
                    alt="QRコードの画像"
                    className="qr-tag-code-image__img"
                  />
                  <a
                    href={qrTag.qr_code_info.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="qr-tag-code-image__link"
                  >
                    画像を新しいタブで開く
                  </a>
                </div>
              )}
            </div>
            <div className="qr-tag-actions">
              <button
                className="btn btn-link"
                onClick={downloadCsv}
                type="button"
                disabled={downloading}
              >
                CSVをダウンロード
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={reloadAll}
              >
                最新情報を取得
              </button>
            </div>
            {error && (
              <div className="qr-tag-error">
                {error}
              </div>
            )}
            {downloadError && (
              <div className="qr-tag-error">
                {downloadError}
              </div>
            )}
            <QrTagEventsMap
              events={events}
              selectedEventId={selectedEventId}
              onSelectEvent={(eventId) => setSelectedEventId(eventId)}
            />
            <div className="qr-tag-timeline">
              <div className="timeline-title">最新イベント（最大10件）</div>
              {events.length === 0 && !error && (
                <div className="timeline-empty">記録がありません</div>
              )}
              <ul className="timeline-list">
                {events.map((event) => (
                  <li
                    key={event.id}
                    className={[
                      'timeline-item',
                      hasLocation(event) ? 'timeline-item--selectable' : '',
                      hasLocation(event) && selectedEventId === event.id ? 'timeline-item--selected' : '',
                    ].filter(Boolean).join(' ')}
                    style={buildTimelineItemStyle(event)}
                  >
                    {hasLocation(event) ? (
                      <button
                        type="button"
                        className="timeline-item__interactive"
                        onClick={() => handleTimelineSelect(event)}
                      >
                        <div className="timeline-item-header">
                          <div className="timeline-event-type">{event.event_type}</div>
                          <div className="timeline-occurred-at">
                            {event.occurred_at ? getFormattedDate(event.occurred_at) : '-'}
                          </div>
                        </div>
                        <div className="timeline-body">
                          <div>
                            <span className="label">場所</span>
                            <span>{formatLocation(event)}</span>
                          </div>
                          <div>
                            <span className="label">記録者</span>
                            <span>{formatRecorder(event)}</span>
                          </div>
                          <div>
                            <span className="label">メモ</span>
                            <span>{event.memo ?? event.notes ?? 'なし'}</span>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="timeline-item__content">
                        <div className="timeline-item-header">
                          <div className="timeline-event-type">{event.event_type}</div>
                          <div className="timeline-occurred-at">
                            {event.occurred_at ? getFormattedDate(event.occurred_at) : '-'}
                          </div>
                        </div>
                        <div className="timeline-body">
                          <div>
                            <span className="label">場所</span>
                            <span>{formatLocation(event)}</span>
                          </div>
                          <div>
                            <span className="label">記録者</span>
                            <span>{formatRecorder(event)}</span>
                          </div>
                          <div>
                            <span className="label">メモ</span>
                            <span>{event.memo ?? event.notes ?? 'なし'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DetailQrTagModal
